<template>
  <div class="banner-message-overlay" @click.self="$emit('close')">
    <div :class="['banner-message', type]">
      <div class="banner-icon-wrapper">
        <div :class="['banner-icon', type]">
          <svg v-if="type === 'success'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
          <svg v-if="type === 'error'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
          <svg v-if="type === 'warning'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
      </div>
      <div class="banner-content">
        <h3 :class="['banner-title', type]">
          {{ type === 'success' ? 'Sucesso!' : type === 'error' ? 'Erro!' : 'Alerta!' }}
        </h3>
        <p class="banner-message-text">{{ message }}</p>
      </div>
      <button :class="['ok-btn', type]" @click="$emit('close')">
        <span>OK</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, onMounted } from 'vue';

const props = defineProps({
  type: {
    type: String,
    default: 'success',
    validator: v => ['success', 'error', 'warning'].includes(v)
  },
  message: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['close']);

// Fechar automaticamente após 3 segundos para mensagens de erro e alerta, 2 segundos para sucesso
onMounted(() => {
  const timeout = props.type === 'error' || props.type === 'warning' ? 3000 : 2000;
  setTimeout(() => {
    emit('close');
  }, timeout);
});
</script>

<style scoped>
.banner-message-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease-out;
}

.banner-message {
  min-width: 380px;
  max-width: 90vw;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  animation: slideUp 0.3s ease-out;
  position: relative;
}

.banner-message::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}

.banner-message.success::before {
  background: linear-gradient(90deg, #10b981 0%, #34d399 100%);
}

.banner-message.error::before {
  background: linear-gradient(90deg, #ef4444 0%, #f87171 100%);
}

.banner-message.warning::before {
  background: linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%);
}

.banner-icon-wrapper {
  display: flex;
  justify-content: center;
  padding: 24px 24px 0;
}

.banner-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.banner-icon.success {
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.4);
}

.banner-icon.error {
  background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.4);
}

.banner-icon.warning {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  box-shadow: 0 4px 14px rgba(245, 158, 11, 0.4);
}

.banner-icon svg {
  width: 32px;
  height: 32px;
  color: white;
  animation: scaleIn 0.4s ease-out 0.1s both;
}

.banner-content {
  padding: 20px 32px 24px;
  text-align: center;
}

.banner-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 8px;
  letter-spacing: -0.025em;
}

.banner-title.success {
  color: #059669;
}

.banner-title.error {
  color: #dc2626;
}

.banner-title.warning {
  color: #d97706;
}

.banner-message-text {
  font-size: 1rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
}

.ok-btn {
  width: 100%;
  padding: 16px 24px;
  font-size: 1rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  position: relative;
  overflow: hidden;
  background: transparent;
}

.ok-btn.success {
  color: #059669;
}

.ok-btn.success:hover {
  background: rgba(16, 185, 129, 0.05);
}

.ok-btn.error {
  color: #dc2626;
}

.ok-btn.error:hover {
  background: rgba(239, 68, 68, 0.05);
}

.ok-btn.warning {
  color: #d97706;
}

.ok-btn.warning:hover {
  background: rgba(245, 158, 11, 0.05);
}

.ok-btn span {
  position: relative;
  z-index: 1;
}

.ok-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.ok-btn.success::before {
  background: rgba(16, 185, 129, 0.1);
}

.ok-btn.error::before {
  background: rgba(239, 68, 68, 0.1);
}

.ok-btn.warning::before {
  background: rgba(245, 158, 11, 0.1);
}

.ok-btn:active::before {
  width: 300px;
  height: 300px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(20px) scale(0.95);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* Responsividade */
@media (max-width: 480px) {
  .banner-message {
    min-width: 320px;
  }
  
  .banner-content {
    padding: 16px 24px 20px;
  }
  
  .banner-title {
    font-size: 1.25rem;
  }
  
  .banner-message-text {
    font-size: 0.95rem;
  }
}

/* Dark mode support - usando classes .dark em vez de @media */
.dark .banner-message {
  background: #1f2937;
  border: 1px solid #374151;
}

.dark .banner-title.success {
  color: #34d399;
}

.dark .banner-title.error {
  color: #f87171;
}

.dark .banner-title.warning {
  color: #fbbf24;
}

.dark .banner-message-text {
  color: #d1d5db;
}

.dark .ok-btn.success {
  color: #34d399;
}

.dark .ok-btn.success:hover {
  background: rgba(52, 211, 153, 0.1);
}

.dark .ok-btn.error {
  color: #f87171;
}

.dark .ok-btn.error:hover {
  background: rgba(248, 113, 113, 0.1);
}

.dark .ok-btn.warning {
  color: #fbbf24;
}

.dark .ok-btn.warning:hover {
  background: rgba(251, 191, 36, 0.1);
}
</style> 